import React, { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, onSnapshot, getDocs, serverTimestamp, doc, deleteDoc } from 'firebase/firestore';
import { writeUserDocument, deleteUserDocument, uploadImageToStorage, moveUserDocument, updateProfileUsage } from '@services/firebaseUser';
import { readGlobalProfiles, writeGlobalProfile as writeGlobalDocument, fetchCombinations, readFlatTemplate } from '@services/firebaseGlobal';
import { getFirebase } from '@services/firebase';
import CustomLargeCardUser from '@components/CustomLargeCardUser';
import CustomLargeCardSuper from '@components/CustomLargeCardSuper';
import CustomProfileCard from '@components/CustomProfileCard';
import { FaPlus, FaSlidersH, FaEdit, FaBirthdayCake } from 'react-icons/fa';
import '../index.css';
import { useProfileActions } from '../hooks/useProfileActions';
import ProcessGuidePanel from '@components/ProcessGuidePanel';
import { useToast } from '../contexts/ToastContext';
import { useAlert } from '../contexts/ToastContext';

const CUSTOM_BUYER_STEPS = [
  { label: 'Edit Added Profile',  sub: 'Customise your requirement',   icon: <FaEdit />,         action: true, info: { image: '/custom-qty-help.png', darkImage: '/custom-qty-help-dark.png', notes: [
    'Up to three images can be attached to the design created (\'+\' button) in Custom page.',
    'To design added from Home or Favourite, setting the quantity allows Flavour alteration.',
    'Enabled for design created (\'+\' button) in Custom page. 2 Tier ≥1.5 kg / 3 Tier ≥2.5 kg',
  ] } },
  { label: 'Create New Profile',  sub: 'Build a design from scratch',  icon: <FaPlus />,         or: true     },
  { label: 'Add to Enquiry',      sub: 'Start enquiry and order',      icon: <FaBirthdayCake />, final: true  },
];

const { auth, db } = getFirebase();

const Custom = () => {
  const [user] = useAuthState(auth);
  const showToast = useToast();
  const showAlert = useAlert();
  const { userRole, customProfiles } = useProfileActions();
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showOngoingModal, setShowOngoingModal] = useState(false);
  const [availableFlavours, setAvailableFlavours] = useState([]);
  const [availableFillings, setAvailableFillings] = useState([]);
  const [availableSponges, setAvailableSponges] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [coverCombinations, setCoverCombinations] = useState([]);
  const [cEvents, setCEvents] = useState([]);
  const [cShapes, setCShapes] = useState([]);
  const [cakeChargeData, setCakeChargeData] = useState({});
  const [personServedData, setPersonServedData] = useState({});

  // Re-open CustomLargeCardSuper if returning from ImageStudio with a modified image
  useEffect(() => {
    const returnedImage = sessionStorage.getItem('studio_returned_image');
    const returnedProfileId = sessionStorage.getItem('studio_return_profile_id');
    if (!returnedImage || !customProfiles) return;
    const targetProfile = returnedProfileId
      ? (customProfiles || []).find(p => p.id === returnedProfileId)
      : null;
    if (returnedProfileId && !targetProfile) return; // profiles not loaded yet
    // Do NOT clear sessionStorage here — CustomLargeCardSuper will read it on mount
    // to inject the returned image into formData (while keeping initialFormData = original)
    openProfileCard(targetProfile || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customProfiles]);

  // Fetch cakeChargeData on mount so profile cards always have it available
  useEffect(() => {
    const fetchChargeData = async () => {
      try {
        const chargeItems = await fetchCombinations('cCakeCharge');
        const chargeMap = {};
        const psMap = {};
        chargeItems.forEach(({ variant, cost, personServed }) => {
          if (variant) {
            chargeMap[variant] = cost;
            if (personServed) psMap[variant] = personServed;
          }
        });
        setCakeChargeData(chargeMap);
        setPersonServedData(psMap);
      } catch (err) {
        console.error('Error fetching cake charge data:', err.message);
      }
    };
    if (user) fetchChargeData();
  }, [user]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedCombinations = await fetchCombinations('oCombination');
        setCombinations(fetchedCombinations);

        const fetchedCoverCombinations = await fetchCombinations('oCoverCombi');
        setCoverCombinations(fetchedCoverCombinations);

        const fetchedCEvents = await readFlatTemplate('cEvents');
        setCEvents(fetchedCEvents);

        const fetchedCShapes = await readFlatTemplate('cShape');
        setCShapes(fetchedCShapes);

        // Process combinations to extract flavours, fillings, and sponges
        const flavours = [...new Set(fetchedCombinations.map(combo => combo.flavour).filter(Boolean))];
        const fillings = [...new Set(fetchedCombinations.map(combo => combo.filling).filter(Boolean))];
        const sponges = [...new Set(fetchedCombinations.flatMap(combo => combo.sponge).filter(Boolean))];

        setAvailableFlavours(flavours);
        setAvailableFillings(fillings);
        setAvailableSponges(sponges);

        if (flavours.length === 0) console.warn('No flavours found.');
        if (fillings.length === 0) console.warn('No fillings found.');
        if (sponges.length === 0) console.warn('No sponges found.');
        if (fetchedCoverCombinations.length === 0) console.warn('No cover combinations found in oCoverCombi.');
      } catch (err) {
        console.error('Error fetching available fields:', err.message, err.stack);
        setError('We couldn’t load the options for creating a profile. Please try again later.');
      }
    };

    if (showProfileCard) {
      // Fetch data when the profile card opens
      fetchData();
    } else {
      // Clear data when the profile card closes
      setAvailableFlavours([]);
      setAvailableFillings([]);
      setAvailableSponges([]);
      setCombinations([]);
      setCoverCombinations([]);
      setCEvents([]);
      setCShapes([]);
      // keep cakeChargeData and personServedData — fetched on mount, not per-card-open
    }
  }, [showProfileCard]);

  const handleAddProfile = async (formData) => {
    if (!user || userRole === 'Admin') return;
    try {
      if (userRole === 'Super') {
        let imageUrl = '';
        if (formData.cimage && formData.cimage.startsWith('data:')) {
          const response = await fetch(formData.cimage);
          const blob = await response.blob();
          imageUrl = await uploadImageToStorage(user.uid, blob);
        } else {
          imageUrl = formData.cimage;
        }

        const docRef = doc(collection(db, 'custom'));
        const profileData = {
          theme: formData.theme,
          cflavour: formData.cflavour,
          cover: formData.cover,
          cfilling: formData.cfilling,
          frosting: formData.frosting,
          sponge: formData.sponge,
          ccost: stripEuro(formData.ccost) || null,
          costest: stripEuro(formData.costest) || null,
          cimage: imageUrl,
          cevent: formData.cevent || [],
          cshape: formData.cshape || [],
          isFromHome: false,
        };

        await writeUserDocument('custom', user.uid, profileData, docRef.id);
        //showAlert('Profile added successfully!');
        setShowProfileCard(false);
        setSelectedProfile(null);
      } else {
        let imageUrls = [];
        if (formData.images && formData.images.length > 0) {
          for (const image of formData.images) {
            if (image && image.startsWith('data:')) {
              const response = await fetch(image);
              const blob = await response.blob();
              const imageUrl = await uploadImageToStorage(user.uid, blob);
              imageUrls.push(imageUrl);
            } else {
              imageUrls.push(image);
            }
          }
        }

        const profileData = {
          cover: formData.cover || null,
          frosting: formData.frosting || null,
          eggless: formData.eggless || false,
          glutenFree: formData.glutenFree || false,
          quantity: formData.quantity || null,
          ccost: stripEuro(formData.ccost) || null,
          costest: stripEuro(formData.costest) || null,
          images: imageUrls,
          tiers: formData.tiers || [],
          tierCount: formData.tierCount || 1,
          multiTier: formData.multiTier || false,
          moreTier: formData.moreTier || false,
          isFromHome: false,
          cshape: formData.cshape || [],
        };

        if (formData.theme) {
          profileData.theme = formData.theme;
        }

        await writeUserDocument('custom', user.uid, profileData);
        //showAlert('Profile added successfully!');
        setShowProfileCard(false);
        setSelectedProfile(null);
      }
    } catch (err) {
      console.error('Error adding custom profile:', err.message, err.stack);
      showAlert(`Failed to add custom profile: ${err.message}`);
    }
  };

  const handleUpdateProfile = async (formData) => {
    if (!user || !selectedProfile) return;
    try {
      if (userRole === 'Super') {
        let imageUrl = selectedProfile.cimage || '';
        if (formData.cimage && formData.cimage.startsWith('data:')) {
          const response = await fetch(formData.cimage);
          const blob = await response.blob();
          imageUrl = await uploadImageToStorage(user.uid, blob);
        } else {
          imageUrl = formData.cimage;
        }

        const updatedData = {
          globalId: selectedProfile.globalId || null, 
          theme: formData.theme,
          cflavour: formData.cflavour,
          cover: formData.cover,
          cfilling: formData.cfilling,
          frosting: formData.frosting,
          sponge: formData.sponge,
          ccost: stripEuro(formData.ccost),
          costest: stripEuro(formData.costest) || null,
          cimage: imageUrl,
          cevent: formData.cevent || [],
          cshape: formData.cshape || [],
          isFromHome: false,
        };
        await writeUserDocument('custom', user.uid, updatedData, selectedProfile.id);
        //showAlert('Profile updated successfully!');
        setShowProfileCard(false);
        setSelectedProfile(null);
      } else {
        let imageUrls = selectedProfile.images || [];
        if (!selectedProfile.isFromHome && formData.images && formData.images.length > 0) {
          imageUrls = [];
          for (const image of formData.images) {
            if (image && image.startsWith('data:')) {
              const response = await fetch(image);
              const blob = await response.blob();
              const imageUrl = await uploadImageToStorage(user.uid, blob);
              imageUrls.push(imageUrl);
            } else {
              imageUrls.push(image);
            }
          }
        }

        const updatedData = {
          cover: formData.cover || null,
          frosting: formData.frosting || null,
          eggless: formData.eggless || false,
          glutenFree: formData.glutenFree || false,
          quantity: formData.quantity || null,
          ccost: stripEuro(formData.ccost) || null,
          costest: stripEuro(formData.costest) || null,
          images: imageUrls,
          tiers: formData.tiers || [],
          tierCount: formData.tierCount || 1,
          multiTier: formData.multiTier || false,
          moreTier: formData.moreTier || false,
          isFromHome: false,
          cshape: selectedProfile.cshape || [],
          globalId: selectedProfile.globalId || null,
        };

        if (formData.theme) {
          updatedData.theme = formData.theme;
        }

        await writeUserDocument('custom', user.uid, updatedData, selectedProfile.id);
        //showAlert('Profile updated successfully!');
        setShowProfileCard(false);
        setSelectedProfile(null);
      }
    } catch (err) {
      console.error('Error updating custom profile:', err.message, err.stack);
      showAlert(`Failed to update custom profile: ${err.message}`);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await deleteUserDocument('custom', user.uid, docId);
      //showAlert('Profile deleted successfully!');
    } catch (err) {
      console.error('Error deleting custom profile:', err.message, err.stack);
      showAlert(`Failed to delete custom profile: ${err.message}`);
    }
  };

  const stripEuro = (v) => v != null ? String(v).replace(/[^0-9.]/g, '') || null : null;

  const handlePublish = async (profile) => {
    try {
      const newTemplate = {
        cflavour: profile.cflavour || profile.tiers?.[0]?.cflavour || null,
        cfilling: profile.cfilling || profile.tiers?.[0]?.cfilling || profile.cfilling1 || null,
        cover: profile.cover || null,
        frosting: profile.frosting || null,
        sponge: profile.sponge || profile.tiers?.[0]?.sponge || null,
        ccost: stripEuro(profile.ccost || profile.costest) || null,
        cimage: profile.cimage || (profile.images && profile.images[0]) || null,
        images: profile.images || [],
        id: profile.id,
        publishedBy: user.uid,
        publishedAt: serverTimestamp(),
        isflag: false,
        cevent: profile.cevent || [],
        cshape: profile.cshape || [],
      };
      if (profile.theme) {
        newTemplate.theme = profile.theme;
      }
      await writeGlobalDocument('ctemplatespublished', profile.id, newTemplate);
      // Delete only the Firestore doc — do NOT delete the storage image since it is
      // now referenced by the published home template.
      const { db: firestoreDb } = getFirebase();
      await deleteDoc(doc(firestoreDb, 'custom', profile.id));
      // Clear inCustom usage flag for the original home template (if this was added from home)
      if (profile.globalId) {
        await updateProfileUsage(profile.globalId, user.uid, 'custom', false).catch((err) =>
          console.warn('updateProfileUsage cleanup after publish failed:', err.message)
        );
      }
      //showAlert('Profile published to home successfully!');
    } catch (err) {
      console.error('Error moving profile to global templates:', err.message, err.stack);
      showAlert(`Failed to publish profile: ${err.message}`);
    }
  };

  const handleAddToCart = async (profile) => {
    try {
      const BLOCKING_STATES = ['Request Review', 'Review Pending', 'Review In-Progress', 'Review Feedback'];
      const enqSnap = await getDocs(query(collection(db, 'enquiry'), where('userId', '==', user.uid)));
      const hasOngoing = enqSnap.docs.some(d => { const s = d.data().reqState; return s && BLOCKING_STATES.includes(s); });
      if (hasOngoing) { setShowOngoingModal(true); return; }
      await moveUserDocument('custom', 'enquiry', user.uid, profile.id);
      showToast('Added to enquiry!');
    } catch (err) {
      console.error('Error moving profile to enquiry:', err.message, err.stack);
      showToast(`Failed to add profile to cart: ${err.message}`);
    }
  };

  const openProfileCard = (profile = null) => {
    setSelectedProfile(profile ? { ...profile, isFromHome: !!profile.isFromHome } : null);
    setShowProfileCard(true);
  };

  const closeProfileCard = () => {
    setShowProfileCard(false);
    setSelectedProfile(null);
  };

  const createdOrUpdatedProfiles = (customProfiles || []).filter(profile => !profile.isFromHome);
  const addedFromHomeOrFavouritesProfiles = (customProfiles || []).filter(profile => profile.isFromHome);

  return (
    <div className="custom-page">
      {!showProfileCard ? (
        <ProcessGuidePanel steps={CUSTOM_BUYER_STEPS} userRole={userRole} column>
          <>
          {(userRole === 'Super' || userRole === 'Buyer') && (
            <div className="add-profile-section" style={{ marginTop: '0' }}>
              <div className="card-container">
                <div className="profile-card-wrapper">
                  <div
                    className="profile-card"
                    onClick={() => openProfileCard()}
                    style={{
                      minHeight: '150px',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <FaPlus style={{ color: '#666', fontSize: 'clamp(24px, 5vw, 36px)' }} />
                  </div>
                </div>
                {createdOrUpdatedProfiles.map(profile => (
                  <div key={profile.id} className="profile-card-wrapper">
                    <CustomProfileCard
                      theme={profile.theme}
                      tiers={profile.tiers || []}
                      cover={profile.cover || 'none'}
                      eggless={profile.eggless || false}
                      glutenFree={profile.glutenFree || false}
                      quantity={profile.quantity || null}
                      ccost={profile.ccost || 'N/A'}
                      costEstimate={profile.costest || null}
                      image={profile.cimage || profile.images?.[0] || ''}
                      tierCount={profile.tierCount || 1}
                      multiTier={profile.multiTier || false}
                      moreTier={profile.moreTier || false}
                      cakeChargeData={cakeChargeData}
                      showPublishIcon={userRole === 'Super'}
                      showOrderIcon={userRole === 'Buyer'}
                      showDeleteIcon={true}
                      isFromHome={profile.isFromHome || false}
                      onPublish={() => handlePublish(profile)}
                      onOrder={() => handleAddToCart(profile)}
                      onDelete={() => handleDelete(profile.id)}
                      onEdit={() => openProfileCard(profile)}
                      userRole={userRole}
                    />
                  </div>
                ))}
              </div>
              <hr style={{ border: '1px solid #ccc', margin: '5px 0 -10px 0' }} />
            </div>
          )}
          {!user ? (
            <p>Please log in to view your custom profiles.</p>
          ) : (
            <div className="card-container">
              {addedFromHomeOrFavouritesProfiles.map(profile => (
                <div key={profile.id} className="profile-card-wrapper">
                  <CustomProfileCard
                    theme={profile.theme}
                    tiers={profile.tiers || []}
                    cover={profile.cover || 'none'}
                    eggless={profile.eggless || false}
                    glutenFree={profile.glutenFree || false}
                    quantity={profile.quantity || null}
                    ccost={profile.ccost || 'N/A'}
                    costEstimate={profile.costest || null}
                    image={profile.cimage || profile.images?.[0] || ''}
                    tierCount={profile.tierCount || 1}
                    multiTier={profile.multiTier || false}
                    moreTier={profile.moreTier || false}
                    cakeChargeData={cakeChargeData}
                    showPublishIcon={userRole === 'Super'}
                    showOrderIcon={userRole === 'Buyer'}
                    showDeleteIcon={true}
                    isFromHome={profile.isFromHome || false}
                    onPublish={() => handlePublish(profile)}
                    onOrder={() => handleAddToCart(profile)}
                    onDelete={() => handleDelete(profile.id)}
                    onEdit={() => openProfileCard(profile)}
                    userRole={userRole}
                  />
                </div>
              ))}
            </div>
          )}
          </>
        </ProcessGuidePanel>
      ) : (
        userRole === 'Super' ? (
          <CustomLargeCardSuper
            profile={selectedProfile}
            userRole={userRole}
            onClose={closeProfileCard}
            onSave={selectedProfile ? handleUpdateProfile : handleAddProfile}
            availableFlavours={availableFlavours}
            availableFillings={availableFillings}
            availableSponges={availableSponges}
            combinations={combinations}
            coverCombinations={coverCombinations}
            cEvents={cEvents}
            cShapes={cShapes}
            cakeChargeData={cakeChargeData}
            personServedData={personServedData}
            readOnly={false}
            isFromHome={selectedProfile?.isFromHome || false}
          />
        ) : (
          <CustomLargeCardUser
            profile={selectedProfile}
            userRole={userRole}
            onClose={closeProfileCard}
            onSave={selectedProfile ? handleUpdateProfile : handleAddProfile}
            availableFlavours={availableFlavours}
            availableFillings={availableFillings}
            availableSponges={availableSponges}
            combinations={combinations}
            coverCombinations={coverCombinations}
            cEvents={cEvents}
            cShapes={cShapes}
            cakeChargeData={cakeChargeData}
            personServedData={personServedData}
            readOnly={false}
            isFromHome={selectedProfile?.isFromHome || false}
          />
        )
      )}
      {showOngoingModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="ongoing-modal-card" style={{ borderRadius: '10px', width: '280px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ background: '#fa4848', color: '#fff', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '14px' }}>
              <span>On-going Enquiry not Finished</span>
            </div>
            <div style={{ padding: '14px', fontSize: '13px', color: '#444' }}>
              <p>You have an active enquiry in progress. Please wait for it to be completed before adding a new request.</p>
            </div>
            <div style={{ padding: '8px 14px 14px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowOngoingModal(false)} style={{ background: '#fa4848', color: '#fff', border: 'none', borderRadius: '5px', padding: '6px 16px', fontSize: '13px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Custom;
